import { Container, FormLabel, Grid, Skeleton, Stack } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FilterProvider } from '../../../components/data-grid/context'
import { PageHeader } from '../../../components/page-header'
import { PageHeaderPanel } from '../../../components/page-header-panel'
import { useFormFilter } from '../../../hooks/form-filter'
import { usePageTitle } from '../../../hooks/title'
import { useTracking } from '../../../hooks/tracking'
import { InventoryItemsList } from './list'
import { StockLevelsFilterViewPanel } from './stock-levels-filter-view-panel'
import { StoreWarehouseFilter } from './store-warehouse-filter'
import { useWarehouseOptions } from './use-warehouse-options'
import { useSearchParams } from 'react-router-dom'

export type QueryParamsState = {
  storeWarehouse?: string
}

export const StockLevelsPage = () => {
  const { t } = useTranslation([
    'shared',
    'stock_levels',
    'products',
    'settings',
  ])
  usePageTitle(t('stock_levels:page_title'))

  const { trackButtonClick } = useTracking()
  const { warehouseOptions, isLoading } = useWarehouseOptions()
  const [firstOption] = warehouseOptions
  const [initalQueryParams] = useSearchParams()
  const { queryParams, formContext } = useFormFilter<QueryParamsState>({
    formProps: {
      values: {
        storeWarehouse:
          initalQueryParams.get('storeWarehouse') ||
          String(firstOption?.id) ||
          '',
      },
    },
  })

  const warehouseId = Number(queryParams.storeWarehouse)
  const [isFilterPanelOpen, setFilterPanelOpen] = useState(false)

  const openFilterPanel = () => {
    setFilterPanelOpen(true)
    trackButtonClick({ name: 'stocktaking-filter-panel-open' })
  }

  const closeFilterPanel = () => {
    setFilterPanelOpen(false)
    trackButtonClick({ name: 'stocktaking-filter-panel-close' })
  }

  const toggleFilterPanel = () => {
    return isFilterPanelOpen ? closeFilterPanel() : openFilterPanel()
  }

  return (
    <FilterProvider>
      <Grid container flexWrap="nowrap">
        <Grid size="grow">
          <PageHeader title={t('stock_levels:page_title')} isFlexible />
          <PageHeaderPanel>
            <Stack
              direction="row"
              gap={1}
              justifyContent="flex-start"
              sx={{ py: 1, minWidth: 300, maxWidth: 450, minHeight: 56 }}
            >
              {isLoading ? (
                <Stack
                  direction="row"
                  gap={1}
                  alignItems="center"
                  width={'100%'}
                >
                  <Skeleton width={72} variant="rectangular" height={24} />
                  <Skeleton width={200} height={36} variant="rectangular" />
                </Stack>
              ) : (
                <FormLabel
                  component={Stack}
                  direction={'row'}
                  gap={1}
                  alignItems="center"
                  width={'100%'}
                >
                  {t('shared:label.warehouse')}
                  <StoreWarehouseFilter
                    name="storeWarehouse"
                    formContext={formContext}
                    warehouseOptions={warehouseOptions}
                  />
                </FormLabel>
              )}
            </Stack>
          </PageHeaderPanel>

          <Container maxWidth={false}>
            <InventoryItemsList
              warehouseId={warehouseId}
              onShowFilter={toggleFilterPanel}
            />
          </Container>
        </Grid>

        <Grid>
          <StockLevelsFilterViewPanel
            isOpen={isFilterPanelOpen}
            onClose={closeFilterPanel}
          />
        </Grid>
      </Grid>
    </FilterProvider>
  )
}
